// =====================================================
// 📁 ActionButton.tsx
// =====================================================

import { ReactNode } from 'react';
import { Loader2 } from 'lucide-react';

export type ActionButtonVariant =
  | 'primary'
  | 'secondary'
  | 'outline'
  | 'success'
  | 'warning'
  | 'danger'
  | 'dark'
  | 'info';

interface ActionButtonProps {
  label: string;
  onClick?: () => void;
  disabled?: boolean;
  loading?: boolean;
  loadingLabel?: string;
  icon?: ReactNode;
  variant?: ActionButtonVariant;
  size?: 'sm' | 'md' | 'lg';
  type?: 'button' | 'submit' | 'reset';
  fullWidth?: boolean;
  className?: string;
  title?: string;
}

const ActionButton = ({
  label,
  onClick,
  disabled = false,
  loading = false,
  loadingLabel,
  icon,
  variant = 'primary',
  size = 'md',
  type = 'button',
  fullWidth = true,
  className = '',
  title,
}: ActionButtonProps) => {
  // =====================================================
  // 🔹 Variantes
  // =====================================================

  const variantClasses: Record<ActionButtonVariant, string> = {
    primary: `
      border
      border-primary
      bg-primary
      text-white
      shadow-sm

      hover:border-primary/90
      hover:bg-primary/90
    `,

    secondary: `
      border
      border-stroke
      bg-white
      text-black
      shadow-sm

      hover:border-primary/40
      hover:bg-gray-2
      hover:text-primary

      dark:border-strokedark
      dark:bg-meta-4
      dark:text-white
      dark:hover:border-primary/50
      dark:hover:bg-boxdark-2
    `,

    outline: `
      border
      border-primary
      bg-transparent
      text-primary

      hover:bg-primary/10

      dark:bg-transparent
      dark:hover:bg-primary/15
    `,

    success: `
      border
      border-success
      bg-success
      text-white
      shadow-sm

      hover:border-success/90
      hover:bg-success/90
    `,

    warning: `
      border
      border-warning
      bg-warning
      text-black
      shadow-sm

      hover:border-warning/90
      hover:bg-warning/90
    `,

    danger: `
      border
      border-danger
      bg-danger
      text-white
      shadow-sm

      hover:border-danger/90
      hover:bg-danger/90
    `,

    dark: `
      border
      border-black
      bg-black
      text-white
      shadow-sm

      hover:bg-black/90

      dark:border-meta-4
      dark:bg-meta-4
      dark:hover:bg-strokedark
    `,

    info: `
      border
      border-meta-5
      bg-meta-5
      text-white
      shadow-sm

      hover:border-meta-5/90
      hover:bg-meta-5/90
    `,
  };

  // =====================================================
  // 🔹 Tamaños
  // =====================================================

  const sizeClasses = {
    sm: `
      min-h-[38px]
      px-3
      py-1.5
      text-xs
    `,

    md: `
      min-h-[44px]
      px-4
      py-2.5
      text-sm
    `,

    lg: `
      min-h-[50px]
      px-5
      py-3
      text-base
    `,
  };

  // =====================================================
  // 🔹 Estado
  // =====================================================

  const isDisabled = disabled || loading;
  const displayedLabel =
    loading && loadingLabel ? loadingLabel : label;

  // =====================================================
  // 🎨 Render
  // =====================================================

  return (
    <button
      type={type}
      title={title}
      disabled={isDisabled}
      aria-busy={loading}
      onClick={onClick}
      className={`
        ${fullWidth ? 'w-full' : ''}

        inline-flex
        items-center
        justify-center
        gap-2

        rounded-xl

        ${sizeClasses[size]}
        ${variantClasses[variant]}

        font-medium

        transition-all
        duration-200

        focus:outline-none
        focus:ring-2
        focus:ring-primary/20
        focus:ring-offset-1

        active:scale-[0.98]

        dark:focus:ring-offset-boxdark

        ${
          isDisabled
            ? `
              cursor-not-allowed
              opacity-50
              active:scale-100
            `
            : ''
        }

        ${className}
      `}
    >
      {loading ? (
        <Loader2 className="h-4 w-4 shrink-0 animate-spin" />
      ) : (
        icon && (
          <span className="flex shrink-0 items-center justify-center">
            {icon}
          </span>
        )
      )}

      <span>{displayedLabel}</span>
    </button>
  );
};

export default ActionButton;